import React from 'react';
import { Box, Typography, Grid, FormControl, TextField, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Fab, Stack, Tooltip } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';

const styles = {
  // Ana Container Stilleri
  sectionContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginBottom: '16px',
  },
  sectionHeader: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    color: '#ffffff',
    fontWeight: 600,
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  sectionContent: {
    padding: '24px',
    backgroundColor: '#fffbeb',
  },
  // Upload Area Stilleri
  uploadArea: {
    border: '2px dashed #fde68a',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    padding: '32px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: '#f59e0b',
      backgroundColor: '#fef3c7',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(245, 158, 11, 0.15)',
    },
  },
  uploadIcon: {
    fontSize: '48px',
    color: '#f59e0b',
    marginBottom: '16px',
  },
  uploadTitle: {
    color: '#334155',
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: '8px',
  },
  uploadSubtitle: {
    color: '#64748b',
    fontSize: '0.875rem',
  },
  // File Item Stilleri
  fileItemCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #fde68a',
    padding: '16px',
    marginBottom: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)',
      borderColor: '#f59e0b',
    },
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  fileIconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileIconPdf: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
  },
  fileIconImage: {
    backgroundColor: '#ecfdf5',
    color: '#059669',
  },
  fileIconDoc: {
    backgroundColor: '#eff6ff',
    color: '#2563eb',
  },
  fileIconDefault: {
    backgroundColor: '#f1f5f9',
    color: '#64748b',
  },
  fileDetails: {
    flex: 1,
  },
  fileName: {
    fontWeight: 600,
    color: '#334155',
    fontSize: '0.95rem',
    marginBottom: '4px',
  },
  fileSize: {
    color: '#64748b',
    fontSize: '0.8rem',
  },
  // Add Button Stilleri
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 0',
    marginTop: '8px',
  },
  addButton: {
    backgroundColor: '#fef3c7',
    color: '#d97706',
    border: '2px dashed #f59e0b',
    borderRadius: '12px',
    padding: '16px 32px',
    fontWeight: 600,
    fontSize: '0.95rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '&:hover': {
      backgroundColor: '#f59e0b',
      color: '#ffffff',
      borderStyle: 'solid',
    },
  },
  // Autocomplete Stilleri
  autocompleteField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      backgroundColor: '#ffffff',
      '&:hover': {
        backgroundColor: '#f8fafc',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
    },
  },
  // Delete Button
  deleteButton: {
    backgroundColor: '#fef2f2 !important',
    color: '#dc2626 !important',
    transition: 'all 0.2s ease !important',
    '&:hover': {
      backgroundColor: '#dc2626 !important',
      color: '#ffffff !important',
    },
  },
  // Table Stilleri (Liste görünümü için)
  tableContainer: {
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },
  tableHead: {
    backgroundColor: '#f8fafc',
    '& .MuiTableCell-head': {
      fontWeight: 600,
      color: '#475569',
      fontSize: '0.875rem',
      padding: '16px',
      borderBottom: '2px solid #e2e8f0',
    },
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
    '& .MuiTableCell-body': {
      padding: '12px 16px',
      borderBottom: '1px solid #e2e8f0',
    },
  },
  downloadChip: {
    backgroundColor: '#ecfdf5',
    color: '#059669',
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#d1fae5',
    },
  },
  // Empty State
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px dashed #fde68a',
  },
  emptyIcon: {
    fontSize: '64px',
    color: '#fcd34d',
    marginBottom: '16px',
  },
  emptyTitle: {
    color: '#92400e',
    fontWeight: 500,
    fontSize: '1rem',
    marginBottom: '8px',
  },
  emptySubtitle: {
    color: '#b45309',
    fontSize: '0.875rem',
  },
  // Loading State
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
  },
  loadingText: {
    marginTop: '16px',
    color: '#64748b',
    fontWeight: 500,
  },
  // Dropzone Custom Styles
  dropzoneContainer: {
    '& .dzu-dropzone': {
      borderRadius: '10px',
      border: '2px dashed #fde68a',
      backgroundColor: '#ffffff',
      minHeight: '80px',
      overflow: 'hidden',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: '#f59e0b',
        backgroundColor: '#fef3c7',
      },
    },
    '& .dzu-inputLabel': {
      color: '#64748b',
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  },
  // Badge for file count
  fileBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: '#ffffff',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
};

const getFileIcon = (fileName) => {
  if (!fileName) return <InsertDriveFileIcon />;

  const ext = fileName.split('.').pop()?.toLowerCase();

  if (['pdf'].includes(ext)) {
    return (
      <Box sx={[styles.fileIconWrapper, styles.fileIconPdf]}>
        <PictureAsPdfIcon />
      </Box>
    );
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return (
      <Box sx={[styles.fileIconWrapper, styles.fileIconImage]}>
        <ImageIcon />
      </Box>
    );
  }
  if (['doc', 'docx'].includes(ext)) {
    return (
      <Box sx={[styles.fileIconWrapper, styles.fileIconDoc]}>
        <DescriptionIcon />
      </Box>
    );
  }
  return (
    <Box sx={[styles.fileIconWrapper, styles.fileIconDefault]}>
      <InsertDriveFileIcon />
    </Box>
  );
};

function SingleFileDropzone({ accept, dropzoneText, maxSize, onFileSelected }) {
  const [isDragActive, setIsDragActive] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const handleFile = (selectedFile) => {
    if (!selectedFile) {
      return;
    }
    if (maxSize && selectedFile.size > maxSize) {
      return;
    }
    onFileSelected(selectedFile);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <Box
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(event) => { event.preventDefault(); setIsDragActive(true); }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragActive(false);
          handleFile(event.dataTransfer.files?.[0]);
        }}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? '#f59e0b' : '#fde68a',
          borderRadius: '10px',
          backgroundColor: isDragActive ? '#fef3c7' : '#ffffff',
          minHeight: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          p: 2
        }}
      >
        <Typography sx={{ color: '#64748b', fontSize: '0.875rem', fontWeight: 500, textAlign: 'center' }}>
          {dropzoneText}
        </Typography>
      </Box>
    </>
  );
}

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Dosya Ekleme Bölümü Componenti (Create için)
export function FileUploadCreate({
  projectFiles,
  setProjectFiles,
  fileTypes,
  data,
  handleFiles,
  handleAddFile,
  checkRolesAuth
}) {
  const activeFiles = projectFiles.filter(x => x.active === 1);
  const isDisabled = checkRolesAuth ? !checkRolesAuth('fileTypeWriteAutocomplete') : false;

  const handleDeleteFile = (fileId) => {
    const updatedFiles = projectFiles.map(f =>
      f.id === fileId ? { ...f, active: 0 } : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleFileTypeChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map(f =>
      f.id === fileId
        ? {
            ...f,
            fileType: newValue?.Id,
            fileSystem: newValue?.Id !== 7 ? 0 : f.fileSystem
          }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleSystemChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map(f =>
      f.id === fileId
        ? { ...f, fileSystem: newValue?.Id ? newValue?.Id : 0 }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  return (
    <Box sx={styles.sectionContainer}>
      {/* Header */}
      <Box sx={styles.sectionHeader}>
        <Typography sx={styles.sectionTitle}>
          <CloudUploadIcon />
          Dosya Ekle
        </Typography>
        {activeFiles.length > 0 && (
          <Box sx={styles.fileBadge}>
            {activeFiles.length} dosya
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={styles.sectionContent}>
        {activeFiles.length === 0 ? (
          <Box sx={styles.emptyState}>
            <CloudUploadIcon sx={styles.emptyIcon} />
            <Typography sx={styles.emptyTitle}>
              Henüz dosya eklenmedi
            </Typography>
            <Typography sx={styles.emptySubtitle}>
              Projenize dosya eklemek için aşağıdaki butona tıklayın
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {activeFiles.map((file) => (
              <Paper key={file.id} sx={styles.fileItemCard} elevation={0}>
                <Grid container spacing={2} alignItems="center">
                  {/* Dosya Tipi Seçimi */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: (file.fileType === 7 || file.fileType === 4) ? 3 : 5
                    }}>
                    <FormControl fullWidth>
                      <Autocomplete
                        disabled={isDisabled}
                        options={fileTypes || []}
                        getOptionLabel={(option) => option.Dsc || ''}
                        value={fileTypes?.find((x) => x.Id === file.fileType) || null}
                        onChange={(event, newValue) => handleFileTypeChange(file.id, newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Dosya Tipi"
                            variant="outlined"
                            size="small"
                            sx={styles.autocompleteField}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {/* Bağlı Sistem Seçimi (fileType === 7 veya 4 ise) */}
                  {(file.fileType === 7 || file.fileType === 4) && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 3
                      }}>
                      <FormControl fullWidth>
                        <Autocomplete
                          disabled={isDisabled}
                          options={
                            data
                              ?.filter((x) => x.Level === 1)
                              .map((item) => ({
                                Id: item.Id,
                                Dsc: item.SystemName
                              })) || []
                          }
                          getOptionLabel={(option) => option.Dsc || ''}
                          value={
                            data?.find((x) => x.Id === file.fileSystem)
                              ? {
                                  Id: file.fileSystem,
                                  Dsc: data.find((x) => x.Id === file.fileSystem)?.SystemName
                                }
                              : null
                          }
                          onChange={(event, newValue) => handleSystemChange(file.id, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Bağlı Sistem"
                              variant="outlined"
                              size="small"
                              sx={styles.autocompleteField}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {/* Dosya Yükleme Alanı */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: 5
                    }}>
                    <Box sx={styles.dropzoneContainer}>
                      <SingleFileDropzone
                        accept="image/*,.pdf,.doc"
                        dropzoneText="Dosya yüklemek için tıklayın veya sürükleyin"
                        maxSize={50000000}
                        onFileSelected={(selectedFile) => handleFiles({ file: selectedFile }, file.id)}
                      />
                    </Box>
                    {file.fileName && (
                      <Box sx={[styles.fileInfo, { mt: 1 }]}>
                        {getFileIcon(file.fileName)}
                        <Box sx={styles.fileDetails}>
                          <Typography sx={styles.fileName}>
                            {file.fileName}
                          </Typography>
                          {file.fileDataSize && (
                            <Typography sx={styles.fileSize}>
                              {formatFileSize(file.fileDataSize)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  {/* Silme Butonu */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: 1
                    }}>
                    <Tooltip title="Dosyayı Sil">
                      <Fab
                        size="small"
                        sx={styles.deleteButton}
                        onClick={() => handleDeleteFile(file.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </Fab>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Dosya Ekle Butonu */}
        <Box sx={styles.addButtonContainer}>
          <Box
            sx={styles.addButton}
            onClick={handleAddFile}
            role="button"
            tabIndex={0}
          >
            <AddCircleOutlineIcon />
            Yeni Dosya Ekle
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Dosya Listesi Bölümü Componenti (Update/View için)
export function FileUploadList({
  projectFiles,
  setProjectFiles,
  fileTypes,
  data,
  handleDownload,
  fileLoading,
  checkRolesAuth
}) {
  const activeFiles = projectFiles.filter((x) => x.fromDb === 1 && x.active === 1);
  const isDisabled = checkRolesAuth ? !checkRolesAuth('fileTypeReadAutocomplete') : false;

  const handleDeleteFile = (fileId) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId ? { ...f, active: 0 } : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleFileTypeChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId
        ? {
            ...f,
            fileType: newValue?.Id,
            fileSystem: newValue?.Id !== 7 ? 0 : f.fileSystem
          }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleSystemChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId
        ? { ...f, fileSystem: newValue?.Id ? newValue?.Id : 0 }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  // Loading State
  if (fileLoading) {
    return (
      <Box sx={styles.sectionContainer}>
        <Box sx={styles.sectionHeader}>
          <Typography sx={styles.sectionTitle}>
            <InsertDriveFileIcon />
            Dosya Listesi
          </Typography>
        </Box>
        <Box sx={styles.sectionContent}>
          <Box sx={styles.loadingContainer}>
            <CircularProgress sx={{ color: '#f59e0b' }} />
            <Typography sx={styles.loadingText}>
              Dosyalar yükleniyor...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.sectionContainer}>
      {/* Header */}
      <Box sx={styles.sectionHeader}>
        <Typography sx={styles.sectionTitle}>
          <InsertDriveFileIcon />
          Dosya Listesi
        </Typography>
        {activeFiles.length > 0 && (
          <Box sx={styles.fileBadge}>
            {activeFiles.length} dosya
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={styles.sectionContent}>
        {activeFiles.length === 0 ? (
          <Box sx={styles.emptyState}>
            <InsertDriveFileIcon sx={styles.emptyIcon} />
            <Typography sx={styles.emptyTitle}>
              Gösterilecek dosya yok
            </Typography>
            <Typography sx={styles.emptySubtitle}>
              Bu proje için henüz dosya eklenmemiş
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={styles.tableContainer} elevation={0}>
            <Table>
              <TableHead sx={styles.tableHead}>
                <TableRow>
                  <TableCell>Dosya Tipi</TableCell>
                  <TableCell>Dosya</TableCell>
                  <TableCell align="center" width={100}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeFiles.map((file) => (
                  <TableRow key={file.id} sx={styles.tableRow}>
                    {/* Dosya Tipi */}
                    <TableCell>
                      <Stack spacing={1}>
                        <Autocomplete
                          fullWidth
                          disabled={isDisabled}
                          options={fileTypes || []}
                          getOptionLabel={(option) => option.Dsc || ''}
                          value={fileTypes?.find((x) => x.Id === file.fileType) || null}
                          onChange={(event, newValue) => handleFileTypeChange(file.id, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Dosya Tipi"
                              variant="outlined"
                              size="small"
                              sx={styles.autocompleteField}
                            />
                          )}
                        />
                        {(file.fileType === 7 || file.fileType === 4) && (
                          <Autocomplete
                            fullWidth
                            disabled={isDisabled}
                            options={
                              data
                                ?.filter((x) => x.Level === 1)
                                .map((item) => ({
                                  Id: item.Id,
                                  Dsc: item.SystemName
                                })) || []
                            }
                            getOptionLabel={(option) => option.Dsc || ''}
                            value={
                              data?.find((x) => x.Id === file.fileSystem)
                                ? {
                                    Id: file.fileSystem,
                                    Dsc: data.find((x) => x.Id === file.fileSystem)?.SystemName
                                  }
                                : null
                            }
                            onChange={(event, newValue) => handleSystemChange(file.id, newValue)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Bağlı Sistem"
                                variant="outlined"
                                size="small"
                                sx={styles.autocompleteField}
                              />
                            )}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    {/* Dosya İndirme */}
                    <TableCell>
                      {file.fileData ? (
                        <Chip
                          icon={<DownloadIcon />}
                          label={file.fileName || 'Dosya İndir'}
                          sx={styles.downloadChip}
                          onClick={() => handleDownload(file.fileData, file.fileName)}
                        />
                      ) : (
                        <Typography sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                          Dosya bulunamadı
                        </Typography>
                      )}
                    </TableCell>

                    {/* Silme */}
                    <TableCell align="center">
                      <Tooltip title="Dosyayı Sil">
                        <Fab
                          size="small"
                          sx={styles.deleteButton}
                          onClick={() => handleDeleteFile(file.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </Fab>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

// Dosya Ekleme Bölümü Componenti (Update için - yeni dosya ekleme)
export function FileUploadAdd({
  projectFiles,
  setProjectFiles,
  fileTypes,
  data,
  handleFiles,
  handleAddFile,
  checkRolesAuth
}) {
  const newFiles = projectFiles.filter((x) => x.fromDb === 0 && x.active === 1);
  const isDisabled = checkRolesAuth ? !checkRolesAuth('fileTypeWriteAutocomplete') : false;
  const isButtonDisabled = checkRolesAuth ? !checkRolesAuth('fileIconButton') : false;

  const handleDeleteFile = (fileId) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId ? { ...f, active: 0 } : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleFileTypeChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId
        ? {
            ...f,
            fileType: newValue?.Id,
            fileSystem: newValue?.Id !== 7 ? 0 : f.fileSystem
          }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  const handleSystemChange = (fileId, newValue) => {
    const updatedFiles = projectFiles.map((f) =>
      f.id === fileId
        ? { ...f, fileSystem: newValue?.Id ? newValue?.Id : 0 }
        : f
    );
    setProjectFiles(updatedFiles);
  };

  return (
    <Box sx={styles.sectionContainer}>
      {/* Header */}
      <Box sx={styles.sectionHeader}>
        <Typography sx={styles.sectionTitle}>
          <CloudUploadIcon />
          Yeni Dosya Ekle
        </Typography>
        {newFiles.length > 0 && (
          <Box sx={styles.fileBadge}>
            {newFiles.length} yeni dosya
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={styles.sectionContent}>
        {newFiles.length > 0 && (
          <Stack spacing={2}>
            {newFiles.map((file) => (
              <Paper key={file.id} sx={styles.fileItemCard} elevation={0}>
                <Grid container spacing={2} alignItems="center">
                  {/* Dosya Tipi Seçimi */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: (file.fileType === 7 || file.fileType === 4) ? 3 : 5
                    }}>
                    <FormControl fullWidth>
                      <Autocomplete
                        disabled={isDisabled}
                        options={fileTypes || []}
                        getOptionLabel={(option) => option.Dsc || ''}
                        value={fileTypes?.find((x) => x.Id === file.fileType) || null}
                        onChange={(event, newValue) => handleFileTypeChange(file.id, newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Dosya Tipi"
                            variant="outlined"
                            size="small"
                            sx={styles.autocompleteField}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  {/* Bağlı Sistem Seçimi */}
                  {(file.fileType === 7 || file.fileType === 4) && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 3
                      }}>
                      <FormControl fullWidth>
                        <Autocomplete
                          disabled={isDisabled}
                          options={
                            data
                              ?.filter((x) => x.Level === 1)
                              .map((item) => ({
                                Id: item.Id,
                                Dsc: item.SystemName
                              })) || []
                          }
                          getOptionLabel={(option) => option.Dsc || ''}
                          value={
                            data?.find((x) => x.Id === file.fileSystem)
                              ? {
                                  Id: file.fileSystem,
                                  Dsc: data.find((x) => x.Id === file.fileSystem)?.SystemName
                                }
                              : null
                          }
                          onChange={(event, newValue) => handleSystemChange(file.id, newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Bağlı Sistem"
                              variant="outlined"
                              size="small"
                              sx={styles.autocompleteField}
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {/* Dosya Yükleme Alanı */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: 5
                    }}>
                    <Box sx={styles.dropzoneContainer}>
                      <SingleFileDropzone
                        accept="image/*,.pdf,.doc"
                        dropzoneText="Dosya yüklemek için tıklayın veya sürükleyin"
                        maxSize={50000000}
                        onFileSelected={(selectedFile) => handleFiles({ file: selectedFile }, file.id)}
                      />
                    </Box>
                    {file.fileName && (
                      <Box sx={[styles.fileInfo, { mt: 1 }]}>
                        {getFileIcon(file.fileName)}
                        <Box sx={styles.fileDetails}>
                          <Typography sx={styles.fileName}>
                            {file.fileName}
                          </Typography>
                          {file.fileDataSize && (
                            <Typography sx={styles.fileSize}>
                              {formatFileSize(file.fileDataSize)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  {/* Silme Butonu */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: 1
                    }}>
                    <Tooltip title="Dosyayı Sil">
                      <Fab
                        size="small"
                        sx={styles.deleteButton}
                        onClick={() => handleDeleteFile(file.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </Fab>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Dosya Ekle Butonu */}
        <Box sx={styles.addButtonContainer}>
          <Box
            sx={styles.addButton}
            onClick={isButtonDisabled ? undefined : handleAddFile}
            role="button"
            tabIndex={0}
            style={{ opacity: isButtonDisabled ? 0.5 : 1, cursor: isButtonDisabled ? 'not-allowed' : 'pointer' }}
          >
            <AddCircleOutlineIcon />
            Yeni Dosya Ekle
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default FileUploadCreate;
